import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@roger/ui-components';

import { overridePaymentOasisHold } from '../../services/api';
import type { PaymentOasisHoldOverrideReason } from '../../services/api/types.gen';
import { billingKeys } from '../../services/queryClient';
import { useToast } from '../../hooks/useToastContext';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';

export const OVERRIDE_HOLD_EXPLANATION =
  'The claim will be created on the imported OASIS before the External EMR exports it. Medicare returns a claim sent before its OASIS is accepted in iQIES. Confirm acceptance on the OASIS Final Validation Report before you send the claim.';

const REASON_MAX_LENGTH = 1000;

const REASON_OPTIONS: {
  value: PaymentOasisHoldOverrideReason;
  label: string;
}[] = [
  { value: 'external_emr_access_lost', label: 'External EMR access lost' },
  { value: 'other', label: 'Other' },
];

/**
 * Bill past a payment-OASIS export hold.
 *
 * The hold stops a draft claim whose payment OASIS is an imported assessment
 * the External EMR has not exported yet. The caller renders this only for a
 * biller who holds `claims:override-oasis-hold`; the route re-checks it.
 *
 * On success it reloads every claims list and the tab counts, the same refresh
 * the draft tabs run after Create Claim, because the override can move the
 * claim from Not Ready to Ready. `onOverridden` lets the caller refresh its own
 * copy of the row and evidence.
 */
export const PaymentOasisHoldOverride: React.FC<{
  billingCycleId: string;
  onOverridden?: () => void | Promise<void>;
}> = ({ billingCycleId, onOverridden }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reasonCode, setReasonCode] = useState<PaymentOasisHoldOverrideReason>(
    'external_emr_access_lost'
  );
  const [reason, setReason] = useState('');
  const queryClient = useQueryClient();
  const { success: showSuccess, error: showError } = useToast();

  useEffect(() => {
    if (open) {
      setReasonCode('external_emr_access_lost');
      setReason('');
    }
  }, [open]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const trimmed = reason.trim();
      const result = await overridePaymentOasisHold({
        path: { billing_cycle_id: billingCycleId },
        body: { reason_code: reasonCode, reason: trimmed || null },
      });
      // The whole result: getBusinessErrorMessage reads the status from
      // `result.response` to keep a 5xx body off the screen.
      if (result.error) throw result;
    } catch (error) {
      console.error('Failed to override the payment OASIS hold:', error);
      showError(
        'Unable to override the hold',
        getBusinessErrorMessage(error) ?? 'Please try again.'
      );
      setSubmitting(false);
      return;
    }

    showSuccess('Hold overridden');
    setOpen(false);
    setSubmitting(false);
    try {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: billingKeys.billingCycles(),
        }),
        queryClient.invalidateQueries({ queryKey: billingKeys.billingMeta() }),
        onOverridden?.(),
      ]);
    } catch (error) {
      console.error('Failed to refresh after overriding the hold:', error);
    }
  };

  return (
    <>
      <Button
        type='button'
        variant='outline'
        size='sm'
        onClick={() => setOpen(true)}
      >
        Override hold
      </Button>
      <Dialog
        open={open}
        onOpenChange={isOpen => {
          if (!isOpen && !submitting) setOpen(false);
        }}
      >
        <DialogContent className='sm:max-w-[425px]'>
          <DialogHeader>
            <DialogTitle>Override export hold</DialogTitle>
            <DialogDescription>{OVERRIDE_HOLD_EXPLANATION}</DialogDescription>
          </DialogHeader>
          <div className='flex flex-col gap-3 py-2'>
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='payment-oasis-hold-reason-code'>Reason</Label>
              <Select
                value={reasonCode}
                onValueChange={value =>
                  setReasonCode(value as PaymentOasisHoldOverrideReason)
                }
                disabled={submitting}
              >
                <SelectTrigger
                  id='payment-oasis-hold-reason-code'
                  selectSize='sm'
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REASON_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='payment-oasis-hold-reason'>
                Reason (optional)
              </Label>
              <Textarea
                id='payment-oasis-hold-reason'
                value={reason}
                onChange={event => setReason(event.target.value)}
                textareaSize='sm'
                rows={3}
                maxLength={REASON_MAX_LENGTH}
                disabled={submitting}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              disabled={submitting}
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type='button'
              disabled={submitting}
              onClick={() => void submit()}
            >
              Override hold
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PaymentOasisHoldOverride;
